import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import {
  Maybe,
  UserCollection,
  UserEntityConnection
} from 'types/graphql-api.generated';

import { addEntityToCollection, getUserCollection } from 'common/api/GraphApi';
import Dropdown from 'common/components/Dropdown';
import get from 'common/tools/objects/get';
import trans from 'common/tools/translations/trans';

import AddToCollectionModalButtonsBar from 'website/components/user/AddToCollectionModalButtonsBar';
import { Entity, SocialAction } from 'website/types';

import styles from './styles.module.scss';

type AddToExistingCollectionProps = {
  entity?: Entity | SocialAction;
  onClose: () => void;
  onNewCollectionRequest: () => void;
};

type Collection = Pick<UserCollection, '__typename' | 'id' | 'name'> & {
  entities?: Maybe<Pick<UserEntityConnection, '__typename' | 'totalCount'>>;
};

const AddToExistingCollection = ({
  entity,
  onClose,
  onNewCollectionRequest
}: AddToExistingCollectionProps) => {
  const [collections, setCollections] = useState<Array<Collection>>([]);
  const [collection, setCollection] = useState<Collection | null>(null);

  const handleSubmit = () => {
    addEntityToCollection(collection, entity?.id);
    onClose();
  };

  const fetchUserCollection = async () => {
    const userCollection = await getUserCollection(entity?.id);
    const getCollections: Array<Collection> =
      get(userCollection, ['me', 'user', 'social', 'collections']) ?? [];
    const firstCollection = getCollections.length ? getCollections[0] : null;

    setCollections(getCollections);
    setCollection(firstCollection);
  };

  // we cannot use an async function directly inside useEffect. If we ever want to call an async function,
  // we need to define the function outside useEffect and then call it within useEffect
  const fetchData = () => {
    fetchUserCollection();
  };

  // Replace componentDidMount
  useEffect(fetchData, []);

  const handleClick = (collectionItem: Collection) => {
    setCollection(collectionItem);
  };

  const DropdownItemRenderer = (collectionItem: Collection) =>
    collectionItem ? (
      <>
        <span>{collectionItem.name}</span>
        {collectionItem.entities?.totalCount ? (
          <span className={styles.addToCollectionAlreadyAdded}>
            {trans('userspace.collections.already-added')}
          </span>
        ) : null}
      </>
    ) : null;

  // list is load inside another ul list
  // to limit height size and have scrollbar instead
  const DropdownList = () =>
    collections.length ? (
      <li>
        <ul className={styles.addToCollectionSelectList}>
          {collections.map(collectionItem => (
            <li
              className={styles.addToCollectionSelectItem}
              key={collectionItem.name}
              onClick={handleClick.bind(null, collectionItem)}
            >
              {DropdownItemRenderer(collectionItem)}
            </li>
          ))}
        </ul>
      </li>
    ) : null;

  const DropdownItems = () => (
    <>
      {DropdownList()}
      <li
        className={classnames(
          'icon',
          'icon-plus-bold',
          styles.addToNewCollectionButton
        )}
        onClick={onNewCollectionRequest}
      >
        {trans('userspace.collections.new-collection')}
      </li>
    </>
  );

  return (
    <>
      <div className={styles.addToCollectionContainer}>
        <label className={classnames('font-1', styles.addToCollectionLabel)}>
          {trans('userspace.collections.choose-collection')}
        </label>
        <Dropdown
          className={classnames('font-1', styles.addToCollectionSelect)}
          value={collection}
          valueRenderer={DropdownItemRenderer}
        >
          {DropdownItems}
        </Dropdown>
      </div>
      <AddToCollectionModalButtonsBar
        onClick={handleSubmit}
        onClose={onClose}
      />
    </>
  );
};

export default AddToExistingCollection;
